import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (!this.#checkFormValidity()) return;

    this.animateAddToCart();

    const enableFlyToCart = this.dataset.enableFlyToCart !== 'false';

    if (enableFlyToCart && !event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Checks if the form is valid when the user adds an item to cart.
   * Currently only checks the gift card recipient form.
   * @returns {boolean} - True if the form is valid, false otherwise.
   */
  #checkFormValidity() {
    const form = this.closest('form');
    if (!form) return true;

    const allInputs = Array.from(form.querySelectorAll('input, select, textarea')).filter((input) =>
      input.id.includes('Recipient')
    );
    let allInputsValid = true;
    for (const input of allInputs) {
      if (
        !(
          input instanceof HTMLInputElement ||
          input instanceof HTMLSelectElement ||
          input instanceof HTMLTextAreaElement
        )
      ) {
        continue;
      }

      // Skip disabled inputs
      if (input.disabled) continue;

      // Check validity on all input elements
      if (!input.checkValidity()) {
        allInputsValid = false;
        break;
      }
    }
    return allInputsValid;
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Initialize button visibility on page load (BIS vs ATC for OOS variants)
    const runInit = () => {
      this.#initializeButtonVisibility();
      // Retry once after rAF so refs/child components (e.g. add-to-cart) are ready
      requestAnimationFrame(() => {
        this.#initializeButtonVisibility();
      });
    };
    setTimeout(runInit, 0);
  }

  /**
   * Calculate if variant can be added to cart based on inventory and availability
   * @param {Object|null} variant - The variant object
   * @returns {boolean} - True if variant can be added to cart
   */
  #calculateCanAddToCart(variant) {
    if (!variant) return false;

    // CRITICAL: Check variant.available FIRST (like old theme)
    // This is Shopify's built-in availability check that accounts for all factors
    if (variant.available === false || variant.available === 'false') {
      return false;
    }

    // Fallback: Also check inventory manually for edge cases
    const inventoryQuantity = variant.inventory_quantity;
    const inventoryPolicy = variant.inventory_policy;
    const inventoryManagement = variant.inventory_management;
    const inventoryManaged = inventoryManagement === 'shopify' || inventoryManagement === true;

    if (inventoryManaged) {
      if (inventoryQuantity !== undefined && inventoryQuantity <= 0 && inventoryPolicy === 'deny') {
        return false;
      }
      if (variant.quantity_rule?.min && inventoryQuantity !== undefined) {
        if (variant.quantity_rule.min > inventoryQuantity && inventoryPolicy === 'deny') {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Check if Back in Stock is enabled for this product
   * @returns {boolean} - True if BIS is enabled
   */
  #isBISEnabled() {
    const buyButtonsBlock = this.closest('.buy-buttons-block');
    const showBackInStock = buyButtonsBlock?.dataset.showBackInStock === 'true';
    const productTags = buyButtonsBlock?.dataset.productTags?.split(',') || [];
    const disableBIS = productTags.some(tag =>
      ['LTD_OLD', 'disable back in stock', 'injection'].includes(tag.trim())
    );
    return showBackInStock && !disableBIS;
  }

  /**
   * Check if there are any available variants in the variant picker
   * @param {HTMLElement} variantPicker - The variant picker element
   * @returns {boolean} True if at least one variant is available
   */
  #checkHasAvailableVariants(variantPicker) {
    if (!variantPicker) return false;

    // Check all variant options to see if any are available
    const variantOptions = variantPicker.querySelectorAll('[data-option-available]');
    if (variantOptions.length === 0) return true; // If no options, assume available (fallback)

    // Check if any option is available
    for (const option of variantOptions) {
      const available = option.dataset.optionAvailable === 'true';
      if (available) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get restock date from selected input in variant picker
   * @returns {string|null} Restock date string or null
   */
  #getRestockDateFromSelectedInput() {
    const variantPicker = this.closest('.product-details, .shopify-section')?.querySelector('variant-picker');
    if (!variantPicker) return null;

    // Try radio inputs first
    let selectedInput = variantPicker.querySelector('input[type="radio"]:checked');
    // If no radio, try select options
    if (!selectedInput) {
      const selectedOption = variantPicker.querySelector('select option[selected]');
      if (selectedOption) {
        selectedInput = selectedOption;
      }
    }

    if (selectedInput && selectedInput instanceof HTMLElement) {
      return selectedInput.dataset.restockDate || null;
    }

    return null;
  }

  /**
   * Check if a restock date is valid and in the future
   * @param {string|null} restockDate - ISO date string (YYYY-MM-DD) or null
   * @returns {boolean} True if date is valid and in the future
   */
  #isValidFutureRestockDate(restockDate) {
    if (!restockDate || typeof restockDate !== 'string') {
      return false;
    }

    const restockDateObj = new Date(restockDate);
    const now = new Date();

    return restockDateObj > now && !isNaN(restockDateObj.getTime());
  }

  /**
   * Update button visibility and state based on variant availability
   * @param {Object|null} variant - The variant object
   */
  #updateButtonState(variant) {
    const bisButton = this.querySelector('#BIS_trigger') || this.querySelector('.BIS_trigger');
    const addToCartButtonContainer =
      this.refs?.addToCartButtonContainer || this.querySelector('add-to-cart-component');
    const currentAddToCartButton = addToCartButtonContainer?.refs?.addToCartButton ||
      addToCartButtonContainer?.querySelector('[ref="addToCartButton"]') ||
      addToCartButtonContainer?.querySelector('button[type="submit"], button[name="add"]');
    const productFormButtons = this.querySelector('.product-form-buttons');
    const quantityBlock = productFormButtons?.querySelector('quantity-selector, .quantity-selector');
    const atcButton = currentAddToCartButton;
    const atcContainer = addToCartButtonContainer;

    const canAddToCart = variant ? this.#calculateCanAddToCart(variant) : false;
    const bisEnabled = this.#isBISEnabled();

    // If we don't have variant data yet, check if variant selection is required
    if (!variant) {
      // Check if product requires variant selection (has variants and not single variant)
      const variantPicker = this.closest('.product-details, .shopify-section')?.querySelector('variant-picker');
      const hasVariants = variantPicker && variantPicker.querySelectorAll('input[type="radio"], select').length > 0;

      const bisEnabled = this.#isBISEnabled();
      let allVariantsUnavailable = false;

      if (hasVariants) {
        // Check if there are any available variants
        const hasAvailableVariants = this.#checkHasAvailableVariants(variantPicker);
        allVariantsUnavailable = !hasAvailableVariants;

        if (hasAvailableVariants) {
          // There are available variants - enable button (inline script will prevent submission until variant selected)
          if (atcButton) {
            atcButton.disabled = false;
          }
          if (addToCartButtonContainer) {
            addToCartButtonContainer.enable();
          }
        } else {
          // All variants are unavailable - disable button
          if (atcButton) {
            atcButton.disabled = true;
          }
          if (addToCartButtonContainer) {
            addToCartButtonContainer.disable();
          }
        }
      }

      // CRITICAL: If all variants are OOS AND BIS is enabled, show BIS button
      if (allVariantsUnavailable && bisEnabled && bisButton) {
        bisButton.style.display = '';
        if (atcContainer) {
          atcContainer.style.display = 'none';
        }
        if (atcButton) {
          atcButton.style.display = 'none';
        }
        if (quantityBlock) {
          quantityBlock.style.display = 'none';
        }
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        // Otherwise hide BIS, show ATC
        if (bisButton) {
          bisButton.style.display = 'none';
        }
        if (atcContainer) {
          atcContainer.style.display = '';
        }
        if (atcButton) {
          atcButton.style.display = '';
        }
        if (quantityBlock) {
          quantityBlock.style.display = '';
        }
      }
      return;
    }

    if (canAddToCart) {
      // Variant is in stock - show ATC button, hide BIS
      if (bisButton) {
        bisButton.style.display = 'none';
      }
      if (atcContainer) {
        atcContainer.style.display = '';
      }
      if (atcButton) {
        atcButton.style.display = '';
      }
      if (quantityBlock) {
        quantityBlock.style.display = '';
      }
      if (addToCartButtonContainer) {
        addToCartButtonContainer.enable();
      }
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    } else if (bisEnabled && bisButton) {
      // Variant is out of stock AND BIS is enabled - show BIS button, hide ATC
      if (variant?.id) {
        // Ensure variant ID is a string (required by some back-in-stock apps)
        bisButton.dataset.variantId = String(variant.id);
        // Also set as attribute to ensure it's available
        bisButton.setAttribute('data-variant-id', String(variant.id));
      }
      bisButton.style.display = '';

      if (atcContainer) {
        atcContainer.style.display = 'none';
      }
      if (atcButton) {
        atcButton.style.display = 'none';
      }
      if (quantityBlock) {
        quantityBlock.style.display = 'none';
      }
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      // Variant is out of stock BUT BIS is NOT enabled - show disabled ATC with "Sold out", hide BIS
      if (bisButton) {
        bisButton.style.display = 'none';
      }
      if (atcContainer) {
        atcContainer.style.display = '';
      }
      if (atcButton) {
        atcButton.style.display = '';
      }
      if (quantityBlock) {
        quantityBlock.style.display = '';
      }
      if (addToCartButtonContainer) {
        addToCartButtonContainer.disable();
      }
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    }
  }

  /**
   * Initialize button visibility based on current variant state
   */
  #initializeButtonVisibility() {
    const productId = this.dataset.productId;
    const sectionOrDetails = this.closest('.product-details, .shopify-section');
    let variantPicker =
      this.closest('variant-picker') ||
      (sectionOrDetails?.querySelector('variant-picker')) ||
      document.querySelector('variant-picker[data-product-id="' + productId + '"]');

    if (!variantPicker && sectionOrDetails) {
      const scripts = sectionOrDetails.querySelectorAll('script[type="application/json"]');
      for (const script of scripts) {
        try {
          const parsed = script.textContent ? JSON.parse(script.textContent) : null;
          if (parsed && typeof parsed.available !== 'undefined' && (parsed.id || parsed.price !== undefined)) {
            let picker = script.closest('variant-picker');
            if (!picker && script.parentElement?.classList?.contains('variant-picker__form')) {
              picker = script.parentElement.parentElement;
            }
            if (!picker) picker = script.parentElement;
            if (picker && (picker.tagName === 'VARIANT-PICKER' || picker.tagName === 'variant-picker')) {
              variantPicker = picker;
              break;
            }
          }
        } catch (_) {
          // ignore
        }
      }
    }

    if (!variantPicker) {
      const buyBlock = this.closest('.buy-buttons-block');
      const initialAvailable = buyBlock?.dataset.initialVariantAvailable;
      const initialVariantId = buyBlock?.dataset.initialVariantId;
      if (initialAvailable !== undefined && initialVariantId) {
        const available = initialAvailable === 'true';
        const syntheticVariant = { id: Number(initialVariantId), available };
        this.#updateButtonState(syntheticVariant);
        return;
      }
      this.#updateButtonState(null);
      return;
    }

    const scriptTag = variantPicker.querySelector('script[type="application/json"]');
    if (!scriptTag || !scriptTag.textContent) {
      this.#updateButtonState(null);
      return;
    }

    try {
      const variant = JSON.parse(scriptTag.textContent);

      // CRITICAL: If variant is null (no variant selected), don't assume it's out of stock
      // Check if there are any available variants first
      if (variant === null) {
        // No variant selected - check if any variants are available
        const hasAvailableVariants = this.#checkHasAvailableVariants(variantPicker);
        if (!hasAvailableVariants) {
          // All variants are unavailable - show out of stock
          this.#updateButtonState(null);
          const currentAddToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
          if (currentAddToCartButton) {
            const textContent = currentAddToCartButton.querySelector('.add-to-cart-text__content');
            if (textContent) {
              const soldOutText = currentAddToCartButton?.dataset.soldOutText ||
                Theme?.translations?.['product.form.sold_out'] ||
                Theme?.translations?.sold_out ||
                'Out of stock';
              textContent.textContent = soldOutText;
            }
          }
        } else {
          // There are available variants - show add to cart button (will be disabled until variant selected)
          this.#updateButtonState(null);
          // Button text should already be "Add to cart" from Liquid, don't override it
        }
        return;
      }

      // Variant is selected - update button state and text
      this.#updateButtonState(variant);

      // CRITICAL: Also update button text on initialization
      const canAddToCart = this.#calculateCanAddToCart(variant);
      const bisEnabled = this.#isBISEnabled();
      const currentAddToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;

      if (currentAddToCartButton) {
        const textContent = currentAddToCartButton.querySelector('.add-to-cart-text__content');
        if (textContent) {
          const addText = Theme?.translations?.add_to_cart || currentAddToCartButton.dataset.addText || 'Add to cart';
          // Use product.form.sold_out translation (matches buy-buttons.liquid)
          const soldOutText = currentAddToCartButton?.dataset.soldOutText ||
            Theme?.translations?.sold_out ||
            'Out of stock';

          if (!canAddToCart) {
            // If BIS is enabled, the BIS button will be shown and ATC hidden
            // So we don't need to update the ATC button text here
            // Only update text if BIS is not enabled
            if (!bisEnabled) {
              textContent.textContent = soldOutText;
            }
          } else {
            textContent.textContent = addText;
          }
        }
      }
    } catch (e) {
      this.#updateButtonState(null);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    // Check both attribute and property (disabled can be set either way)
    const atcButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (atcButton && (atcButton.disabled === true || atcButton.hasAttribute('disabled'))) {
      return;
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
    const mainVariantId = formData.get('id');
    const mainQuantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    const section = this.closest('.shopify-section');
    const upsellBlocks = section ? section.querySelectorAll('[data-upsell-block][data-upsell-add="true"]') : [];
    const upsellVariantIds = [];
    upsellBlocks.forEach((block) => {
      const vid = block.getAttribute('data-upsell-variant-id');
      if (vid) upsellVariantIds.push(vid);
    });

    const tieredRoot = section?.querySelector('[data-tiered-upsell][data-tiered-upsell-enabled="true"]') ?? null;
    const tieredAddonRaw = tieredRoot?.getAttribute('data-selected-addon-variant-ids') || '';
    const tieredAddonVariantIds = tieredAddonRaw.split(',').filter(Boolean);
    const tieredAddonVariantIdSet = new Set(tieredAddonVariantIds);
    /** First add-on in bundle order → _bundleTier1; second → _bundleTier2 (matches tiered block addon order). */
    const tieredAddonSlotByVariantId = new Map();
    tieredAddonVariantIds.forEach((vid, index) => {
      tieredAddonSlotByVariantId.set(String(vid), index);
    });

    const extraVariantIds = [...tieredAddonVariantIds];
    upsellVariantIds.forEach((id) => {
      if (!extraVariantIds.includes(id)) extraVariantIds.push(id);
    });

    const useItemsArray = extraVariantIds.length > 0;
    const mainVariantIdStr = String(mainVariantId);
    const items = [{ id: mainVariantId, quantity: mainQuantity }];
    extraVariantIds.forEach((id) => {
      const isTieredBundleAddon = tieredAddonVariantIdSet.has(id);
      if (isTieredBundleAddon) {
        const slot = tieredAddonSlotByVariantId.get(String(id));
        const bundleSlotProps =
          slot === 0 ? { _bundleTier1: true } : slot === 1 ? { _bundleTier2: true } : {};
        items.push({
          id,
          quantity: 1,
          properties: {
            _tieredPdpBundle: '1',
            _linkedProduct: mainVariantIdStr,
            ...bundleSlotProps,
          },
        });
      } else {
        items.push({
          id,
          quantity: 1,
          properties: {
            __bundle_save: true,
          },
        });
      }
    });

    const cartAddUrl =
      Theme.routes.cart_add_url +
      (cartItemComponentsSectionIds.length ? '?sections=' + cartItemComponentsSectionIds.join(',') : '');

    let fetchOptions;
    if (useItemsArray) {
      fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      };
    } else {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
      const cfg = fetchConfig('javascript', { body: formData });
      fetchOptions = {
        ...cfg,
        headers: {
          ...cfg.headers,
          Accept: 'text/html',
        },
      };
    }

    fetch(cartAddUrl, fetchOptions)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = mainVariantId || formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          const totalItemCount = useItemsArray
            ? items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
            : Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: totalItemCount,
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error('Add-to-cart request failed:', error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;
    const variant = event.detail.resource;
    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) {
      return;
    }

    const canAddToCart = this.#calculateCanAddToCart(variant);
    const bisEnabled = this.#isBISEnabled();

    // Update button visibility and state FIRST (before morph)
    this.#updateButtonState(variant);

    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
      // CRITICAL: After morph, re-apply disabled state if needed
      // Morph might replace the button with one that doesn't have disabled state
      requestAnimationFrame(() => {
        if (!canAddToCart && currentAddToCartButton) {
          currentAddToCartButton.disabled = true;
        }
      });
    }

    // Update button text based on canAddToCart state
    // Use requestAnimationFrame to ensure DOM has updated after morph
    requestAnimationFrame(() => {
      const textContent = currentAddToCartButton?.querySelector('.add-to-cart-text__content');

      if (textContent) {
        // Use Theme.translations for "Add to cart" to ensure we get the full translation
        // The data attribute might have "Add" (short version)
        const addText = Theme?.translations?.add_to_cart || currentAddToCartButton.dataset.addText || 'Add to cart';
        // Use product.form.sold_out translation (matches buy-buttons.liquid)
        const soldOutText = Theme?.translations?.sold_out ||
          currentAddToCartButton?.dataset.soldOutText ||
          'Out of stock';

        // Update button text based on availability
        if (!canAddToCart) {
          // Variant is unavailable - show sold out text
          // Check if BIS button should be shown instead (has restock date)
          if (bisEnabled) {
            let restockDate = variant?.restock_date;
            if (!restockDate) {
              restockDate = this.#getRestockDateFromSelectedInput();
            }
            const hasRestockDate = restockDate && this.#isValidFutureRestockDate(restockDate);

            // If BIS button is showing, ATC button is hidden, so don't update text
            // But if BIS is enabled but no restock date, show sold out text
            if (!hasRestockDate) {
              textContent.textContent = soldOutText;
            }
          } else {
            // BIS not enabled - show sold out text
            textContent.textContent = soldOutText;
          }
        } else if (canAddToCart) {
          textContent.textContent = addText;
        }
      }

      // CRITICAL: Ensure disabled state is correct after all updates
      if (currentAddToCartButton) {
        if (!canAddToCart && !bisEnabled) {
          currentAddToCartButton.disabled = true;
        } else if (canAddToCart) {
          currentAddToCartButton.disabled = false;
        }
      }
    });

    variantId.value = variant?.id ?? '';

    if (variant) {
      const productVariantMedia = variant.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

